import * as React from "react";
import { startOfDay, startOfWeek, format } from "date-fns";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Info, type LucideIcon } from "lucide-react";

import { Hint } from "@/components/analytics/hint";
import { Legend, SegmentBar, VizEmpty, STEP_BG } from "@/components/analytics/viz";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useIsMobile } from "@/hooks/use-mobile";
import { tone as toneMap, type Tone } from "@/lib/tone";
import { cn } from "cn";

// The Summary tab every record page carries (Tasks, Deals, Customers,
// Contacts, Activity). Card shapes come from /widgets-demo and /charts-demo —
// KPI tiles with a sparkline, the distribution panel, segmented tick bars,
// gradient area trends, rounded stacked bars, a weekday heatmap — fed with the
// page's live, filtered rows. Colour follows viz.tsx's rules: status tones for
// state, the ordinal step ramp for ordered buckets, one hue for nominal ranks.

export const DAY = 86_400_000;

// --- Layout -------------------------------------------------------------------

/** Page wrapper: the grid rows, stacked. */
export function SummaryLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      {children}
    </div>
  );
}

/**
 * Equal-height row of cards; `cols` sets the desktop column count. A row of
 * KpiCards goes 2-up on phones rather than stacking four tall tiles.
 */
export function SummaryRow({
  children,
  cols = 2,
  className,
}: {
  children: React.ReactNode;
  cols?: 2 | 3 | 4 | 5;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-4 max-sm:has-[>[data-kpi]]:grid-cols-2 max-sm:has-[>[data-kpi]]:gap-3",
        cols === 2 && "lg:grid-cols-2",
        cols === 3 && "md:grid-cols-2 xl:grid-cols-3",
        cols === 4 && "sm:grid-cols-2 xl:grid-cols-4",
        cols === 5 && "sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5",
        className,
      )}
    >
      {children}
    </div>
  );
}

function TitleIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md border shadow-xs">
      <Icon className="size-4 text-muted-foreground" />
    </span>
  );
}

/**
 * The widget frame: icon chip, title, optional description and action. Method
 * notes and caveats go in `info` — an (i) beside the title — so the card body
 * stays charts and numbers rather than paragraphs.
 */
export function WidgetCard({
  icon,
  title,
  description,
  info,
  action,
  stackAction = false,
  children,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  description?: React.ReactNode;
  info?: React.ReactNode;
  action?: React.ReactNode;
  /**
   * For a wide action (a row of toggles): when the header is narrower than
   * 40rem the action drops under the title instead of overlapping it, and
   * scrolls sideways if it still doesn't fit.
   */
  stackAction?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("h-full", className)}>
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          {icon ? <TitleIcon icon={icon} /> : null}
          <div className="flex min-w-0 flex-col gap-0.5">
            <CardTitle className="flex items-center gap-1.5 leading-tight">
              {title}
              {info ? (
                <Hint label={<span className="block max-w-72">{info}</span>}>
                  <Info className="size-3.5 text-muted-foreground" aria-label="About this card" />
                </Hint>
              ) : null}
            </CardTitle>
            {description ? <CardDescription className="text-xs">{description}</CardDescription> : null}
          </div>
        </div>
        {action ? (
          <CardAction
            className={cn(
              stackAction &&
                "@max-[40rem]/card-header:col-start-1 @max-[40rem]/card-header:row-span-1 @max-[40rem]/card-header:row-start-auto @max-[40rem]/card-header:mt-2 @max-[40rem]/card-header:max-w-full @max-[40rem]/card-header:justify-self-start @max-[40rem]/card-header:overflow-x-auto @max-[40rem]/card-header:[scrollbar-width:none]",
            )}
          >
            {action}
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">{children}</CardContent>
    </Card>
  );
}

// --- KPI tile -------------------------------------------------------------------

/** Headline number with an optional 2px sparkline (StatSparkCards). */
export function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  hintTone,
  spark,
  sparkLabel,
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  /** Colours the hint only — status tones, never the value. */
  hintTone?: Tone;
  spark?: number[];
  sparkLabel?: string;
}) {
  const id = React.useId().replace(/:/g, "");
  return (
    // Phones (2-up, ~165px wide): no icon chip, and label / hint wrap to two
    // lines instead of truncating.
    <Card size="sm" className="h-full" data-kpi="">
      <CardContent className="flex h-full flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium text-muted-foreground max-sm:line-clamp-2 max-sm:text-xs max-sm:whitespace-normal">
            {label}
          </p>
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted max-sm:hidden">
            <Icon className="size-3.5 text-muted-foreground" />
          </span>
        </div>
        <span className="truncate text-2xl font-semibold tracking-tight tabular-nums max-sm:text-xl">{value}</span>
        {hint ? (
          <span
            className={cn(
              "truncate text-xs max-sm:line-clamp-2 max-sm:whitespace-normal",
              hintTone ? toneMap[hintTone].text : "text-muted-foreground",
            )}
          >
            {hint}
          </span>
        ) : null}
        {spark && spark.length > 1 ? (
          <ChartContainer
            config={{ v: { label: sparkLabel ?? label, color: "var(--chart-2)" } }}
            className="mt-auto aspect-auto h-10 w-full pt-1 max-sm:h-8"
          >
            <AreaChart data={spark.map((v, i) => ({ i, v }))} margin={{ left: 0, right: 0, top: 2, bottom: 0 }}>
              <defs>
                <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-v)" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="var(--color-v)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area
                dataKey="v"
                type="monotone"
                stroke="var(--color-v)"
                strokeWidth={1.5}
                fill={`url(#spark-${id})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ChartContainer>
        ) : null}
      </CardContent>
    </Card>
  );
}

// --- Distribution panel ---------------------------------------------------------

export type DistItem = { key: string; label: string; value: number; fill: string; icon?: LucideIcon };

/** Big number, one part-to-whole bar, then a tile per part (ResourceDistribution). */
export function DistributionPanel({
  headline,
  headlineLabel,
  items,
}: {
  headline: React.ReactNode;
  headlineLabel: string;
  items: DistItem[];
}) {
  const total = items.reduce((s, i) => s + i.value, 0);
  return (
    <>
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-medium tracking-tight tabular-nums">{headline}</span>
        <span className="text-sm font-medium text-muted-foreground">{headlineLabel}</span>
      </div>
      <div className="flex flex-col gap-2">
        <SegmentBar
          size="bold"
          segments={items.map((i) => ({ key: i.key, label: i.label, value: i.value, tone: i.fill }))}
        />
        <Legend
          items={items.map((i) => ({
            key: i.key,
            label: i.label,
            tone: i.fill,
            text: total ? `${Math.round((i.value / total) * 100)}%` : "0%",
          }))}
        />
      </div>
      <div className={cn("mt-auto grid grid-cols-2 gap-2", items.length === 3 && "grid-cols-3", items.length >= 4 && "sm:grid-cols-4")}>
        {items.map((i) => (
          <div key={i.key} className="flex flex-col gap-1.5 rounded-md bg-muted/50 p-3">
            <div className="flex items-center gap-2">
              <span className={cn("flex size-6 items-center justify-center rounded-md text-white", i.fill)}>
                {i.icon ? <i.icon className="size-3.5" /> : null}
              </span>
              <span className="text-xl font-medium tracking-tight tabular-nums">{i.value.toLocaleString("en-US")}</span>
            </div>
            <span className="truncate text-xs font-medium text-muted-foreground">{i.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// --- Segmented tick rows ----------------------------------------------------------

const TICKS = 24;

export type TickRow = {
  key: string;
  label: string;
  value: number;
  /** Shown instead of the raw count, e.g. a money amount. */
  text?: React.ReactNode;
  /** Background class for lit ticks. */
  fill: string;
  /** Small chip shown before the label. */
  badge?: React.ReactNode;
  caption?: React.ReactNode;
};

/** One labelled row per bucket, share drawn as lit ticks (RequestStatus). */
export function TickRows({ rows, total: totalOverride }: { rows: TickRow[]; total?: number }) {
  const total = totalOverride ?? rows.reduce((s, r) => s + r.value, 0);
  return (
    <div className="flex flex-col gap-4">
      {rows.map((r) => {
        const pct = total ? (r.value / total) * 100 : 0;
        const on = r.value > 0 ? Math.max(1, Math.round((pct / 100) * TICKS)) : 0;
        return (
          <div key={r.key} className="flex items-center gap-4">
            <div className="flex w-32 shrink-0 flex-col gap-0.5 sm:w-36">
              <div className="flex min-w-0 items-center gap-2">
                {r.badge}
                <span className="truncate text-sm font-medium">{r.label}</span>
              </div>
              <span className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground tabular-nums">{r.text ?? r.value.toLocaleString("en-US")}</span>{" "}
                {r.caption ?? `· ${Math.round(pct)}%`}
              </span>
            </div>
            <Hint
              label={`${r.label} · ${typeof r.text === "string" ? r.text : r.value.toLocaleString("en-US")} (${Math.round(pct)}%)`}
              render={
                <div aria-hidden className="flex h-8 min-w-0 flex-1 gap-0.5">
                  {Array.from({ length: TICKS }, (_, i) => (
                    <span key={i} className={cn("min-w-0 flex-1 rounded-sm", i < on ? r.fill : "bg-muted")} />
                  ))}
                </div>
              }
            />
          </div>
        );
      })}
    </div>
  );
}

// --- Ratio strip ------------------------------------------------------------------

/** Two-or-three-way split with the big shares on top and a muted stat strip below (OutreachRatio). */
export function StatStrip({ stats }: { stats: { label: string; value: React.ReactNode }[] }) {
  return (
    <div className="mt-auto flex items-stretch justify-between gap-2 rounded-md bg-muted/50">
      {stats.map((s, i) => (
        <React.Fragment key={s.label}>
          {i > 0 ? <div className="my-3 w-px bg-border" /> : null}
          <div className="flex min-w-0 flex-1 flex-col items-center gap-1 px-2 py-3 text-center">
            <span className="text-xl font-medium tracking-tight tabular-nums">{s.value}</span>
            <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// --- Trend charts -------------------------------------------------------------------

export type Series = { key: string; label: string; color: string };

/** Trend charts drop to 11rem on phones and on short landscape screens, whatever height the page asked for. */
const PHONE_CHART_H = "max-sm:h-44 [@media(max-height:480px)]:h-44";

/** Gradient area chart over time buckets (Chart1 API traffic). */
export function TrendArea({
  data,
  series,
  xKey = "label",
  height = "h-56",
}: {
  data: Record<string, number | string>[];
  series: Series[];
  xKey?: string;
  height?: string;
}) {
  const id = React.useId().replace(/:/g, "");
  const isMobile = useIsMobile();
  const config = Object.fromEntries(series.map((s) => [s.key, { label: s.label, color: s.color }])) as ChartConfig;
  if (data.every((d) => series.every((s) => !d[s.key]))) return <VizEmpty>Nothing in this window yet.</VizEmpty>;
  return (
    <ChartContainer config={config} className={cn("aspect-auto w-full", height, PHONE_CHART_H)}>
      <AreaChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`fill-${id}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={`var(--color-${s.key})`} stopOpacity={0.5} />
              <stop offset="95%" stopColor={`var(--color-${s.key})`} stopOpacity={0.03} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickMargin={8} minTickGap={isMobile ? 40 : 24} />
        <YAxis tickLine={false} axisLine={false} width={32} allowDecimals={false} tickCount={isMobile ? 3 : undefined} />
        <ChartTooltip cursor={{ strokeDasharray: "3 3" }} content={<ChartTooltipContent indicator="dot" />} />
        {series.map((s) => (
          <Area
            key={s.key}
            dataKey={s.key}
            type="monotone"
            stroke={`var(--color-${s.key})`}
            fill={`url(#fill-${id}-${s.key})`}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--background)" }}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    </ChartContainer>
  );
}

function RoundedBar(props: { x?: number; y?: number; width?: number; height?: number; fill?: string }) {
  const { x = 0, y = 0, width = 0, height = 0, fill } = props;
  if (height <= 0) return null;
  // 2px surface gap between stacked segments.
  return <rect x={x} y={y + 1} width={width} height={Math.max(0, height - 2)} rx={4} ry={4} fill={fill} />;
}

/** Rounded (stacked) bars per time bucket (Chart5 customer growth). */
export function TrendBars({
  data,
  series,
  stacked = false,
  xKey = "label",
  height = "h-56",
  yFormat,
}: {
  data: Record<string, number | string>[];
  series: Series[];
  stacked?: boolean;
  xKey?: string;
  height?: string;
  /** Tick text for large values (money), which the 32px default axis would clip. */
  yFormat?: (v: number) => string;
}) {
  const isMobile = useIsMobile();
  const config = Object.fromEntries(series.map((s) => [s.key, { label: s.label, color: s.color }])) as ChartConfig;
  if (data.every((d) => series.every((s) => !d[s.key]))) return <VizEmpty>Nothing in this window yet.</VizEmpty>;
  return (
    <div className="flex flex-col gap-3">
      <ChartContainer config={config} className={cn("aspect-auto w-full", height, PHONE_CHART_H)}>
        <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }} barGap={2}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickMargin={8} minTickGap={isMobile ? 28 : 12} />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={yFormat ? 44 : 32}
            allowDecimals={false}
            tickCount={isMobile ? 3 : undefined}
            tickFormatter={yFormat}
          />
          <ChartTooltip cursor={{ fill: "var(--muted)", opacity: 0.5 }} content={<ChartTooltipContent indicator="dot" />} />
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId={stacked ? "a" : undefined}
              fill={`var(--color-${s.key})`}
              shape={<RoundedBar />}
              maxBarSize={stacked ? 26 : 14}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ChartContainer>
      {series.length > 1 ? <SeriesLegend series={series} className="justify-center" /> : null}
    </div>
  );
}

/** Legend for recharts series, whose colours are CSS values not classes. */
export function SeriesLegend({ series, className }: { series: Series[]; className?: string }) {
  return (
    <ul className={cn("flex flex-wrap gap-x-4 gap-y-1 text-xs", className)}>
      {series.map((s) => (
        <li key={s.key} className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full ring-1 ring-foreground/15" style={{ background: s.color }} />
          <span className="text-muted-foreground">{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

// --- Heatmap ------------------------------------------------------------------------

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SLOTS = ["6–9", "9–12", "12–15", "15–18", "18–21", "Other"];

function slotOf(hour: number) {
  if (hour >= 6 && hour < 9) return 0;
  if (hour >= 9 && hour < 12) return 1;
  if (hour >= 12 && hour < 15) return 2;
  if (hour >= 15 && hour < 18) return 3;
  if (hour >= 18 && hour < 21) return 4;
  return 5;
}

/** Weekday × time-of-day counts on the ordinal ramp (Chart2 weekly traffic). */
export function WeekHeatmap({ times, noun }: { times: number[]; noun: string }) {
  const grid = React.useMemo(() => {
    const g = WEEKDAYS.map(() => SLOTS.map(() => 0));
    for (const t of times) {
      const d = new Date(t);
      g[(d.getDay() + 6) % 7][slotOf(d.getHours())]++;
    }
    return g;
  }, [times]);
  const max = Math.max(0, ...grid.flat());
  if (max === 0) return <VizEmpty>No {noun} in this window yet.</VizEmpty>;
  const cell = (v: number) => {
    if (v === 0) return "bg-muted";
    const step = Math.min(4, Math.floor((v / max) * 5 - 1e-9));
    return STEP_BG[Math.max(0, step)];
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-[2.5rem_repeat(6,minmax(0,1fr))] gap-1 text-[11px] text-muted-foreground">
        <span />
        {SLOTS.map((s) => (
          <span key={s} className="text-center tabular-nums">
            {s}
          </span>
        ))}
        {WEEKDAYS.map((day, r) => (
          <React.Fragment key={day}>
            <span className="self-center">{day}</span>
            {grid[r].map((v, c) => (
              <Hint
                key={c}
                label={`${day} ${SLOTS[c]} · ${v} ${noun}`}
                render={<span className={cn("h-6 rounded-sm transition-opacity hover:opacity-80", cell(v))} />}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center justify-end gap-1.5 text-[11px] text-muted-foreground">
        Fewer
        {STEP_BG.map((c) => (
          <span key={c} className={cn("size-2.5 rounded-xs", c)} />
        ))}
        More
      </div>
    </div>
  );
}

// --- Bucketing helpers ----------------------------------------------------------------

/** Counts per calendar week for the last `weeks` weeks (Monday start), oldest first. */
export function weekly<T>(
  rows: readonly T[],
  weeks: number,
  now: number,
  series: Record<string, (r: T) => number | undefined>,
) {
  const thisWeek = startOfWeek(now, { weekStartsOn: 1 }).getTime();
  const starts = Array.from({ length: weeks }, (_, i) => thisWeek - (weeks - 1 - i) * 7 * DAY);
  const out = starts.map((s) => {
    const row: Record<string, number | string> = { label: format(s, "MMM d") };
    for (const k of Object.keys(series)) row[k] = 0;
    return row;
  });
  for (const r of rows) {
    for (const [k, get] of Object.entries(series)) {
      const t = get(r);
      if (t === undefined || t < starts[0] || t >= thisWeek + 7 * DAY) continue;
      const i = Math.min(weeks - 1, Math.floor((t - starts[0]) / (7 * DAY)));
      out[i][k] = (out[i][k] as number) + 1;
    }
  }
  return out;
}

/** Counts per day for the last `days` days, oldest first. */
export function daily<T>(
  rows: readonly T[],
  days: number,
  now: number,
  series: Record<string, (r: T) => number | undefined>,
) {
  const today = startOfDay(now).getTime();
  const first = today - (days - 1) * DAY;
  const out = Array.from({ length: days }, (_, i) => {
    const row: Record<string, number | string> = { label: format(first + i * DAY, "MMM d") };
    for (const k of Object.keys(series)) row[k] = 0;
    return row;
  });
  for (const r of rows) {
    for (const [k, get] of Object.entries(series)) {
      const t = get(r);
      if (t === undefined || t < first || t >= today + DAY) continue;
      const i = Math.floor((t - first) / DAY);
      out[i][k] = (out[i][k] as number) + 1;
    }
  }
  return out;
}

/** Group-and-count, largest first; the tail past `top` folds into "Other". */
export function countBy<T>(rows: readonly T[], key: (r: T) => string | undefined, top = 6) {
  const m = new Map<string, number>();
  for (const r of rows) {
    const k = key(r);
    if (!k) continue;
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  const sorted = [...m.entries()].sort((a, b) => b[1] - a[1]);
  if (sorted.length <= top) return sorted;
  const rest = sorted.slice(top - 1).reduce((s, [, n]) => s + n, 0);
  return [...sorted.slice(0, top - 1), ["Other", rest] as [string, number]];
}

export function median(values: number[]) {
  if (values.length === 0) return undefined;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : "—");

/** Chip dot for a tick row badge. */
export function Dot({ className }: { className: string }) {
  return <span className={cn("size-2 shrink-0 rounded-full", className)} />;
}

/** Skeleton grid while rows load. */
export function SummarySkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading summary">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-muted/60" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="h-80 animate-pulse rounded-xl bg-muted/60" />
        <div className="h-80 animate-pulse rounded-xl bg-muted/60" />
      </div>
    </div>
  );
}
